import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { Property } from '~/core/entities/property';
import type {
	Accommodation,
	AggregateOffer,
	Apartment,
	House,
	ImageObject,
	Offer,
	Person,
	Product,
	QuantitativeValue,
	WithContext
} from 'schema-dts';

import { FeatureEnum } from '~/core/entities/CRMEnums';

import { PropertyPresenter } from './PropertyPresenter';

type AccommodationObject = Exclude<Accommodation, string>;
type AccommodationType = Extract<
	AccommodationObject['@type'],
	'Accommodation' | 'Apartment' | 'House'
>;

type ApartmentObject = Exclude<Apartment, string>;
type HouseObject = Exclude<House, string>;

export class PropertyJsonLdPresenter {
	static getProductJsonLd(
		property: Property,
		{ websiteUrl }: { websiteUrl: string }
	): WithContext<Product> | null {
		const offers = this.getPropertyOfferJsonLd(property, { websiteUrl });

		// Google's "Product snippets" require one of offers/review/aggregateRating.
		// Without a price there is no valid offer, so we skip the Product entirely
		// rather than emit an item that can never produce a rich result.
		if (!offers) return null;

		const accommodationJsonLd = this.#getAccommodationJsonLd(property, {
			websiteUrl
		});

		const propertyPageUrl = PropertyPresenter.getPropertyPageUrl(
			property,
			websiteUrl
		);

		return {
			'@type': 'Product',
			'@id': property.commercialId,
			'@context': 'https://schema.org',
			name: property.ad.title,
			description: property.ad.description,
			image: this.getPropertyJsonLdImageObject(property),
			url: propertyPageUrl,
			offers,
			category: accommodationJsonLd
		};
	}

	static getPropertyOfferJsonLd(
		property: Property,
		{ websiteUrl }: { websiteUrl: string }
	): AggregateOffer | Offer | undefined {
		const companyLogo = this.#getPropertyCompanyJsonLdImageObject(property);

		const propertyBrokerJsonLd: Person = {
			'@type': 'Person',
			additionalType: 'RealEstateAgent',
			name: property.agent.name,
			memberOf: {
				'@type': 'Organization',
				additionalType: 'RealEstateAgent',
				'@id': property.company.id,
				name: property.company.name,
				logo: companyLogo,
				image: companyLogo
			}
		};

		const commonPropertyData: Omit<Offer, 'price' | '@type'> = {
			priceCurrency: 'BRL',
			availability: 'https://schema.org/InStock',
			// Reference the Product by @id instead of nesting a full Product:
			// a nested Product without offers is flagged as an invalid snippet.
			itemOffered: { '@id': property.commercialId },
			url: PropertyPresenter.getPropertyPageUrl(property, websiteUrl),
			offeredBy: propertyBrokerJsonLd
		};

		const offers: Offer[] = [
			this.#getPropertyTransactionOffer({
				property,
				data: commonPropertyData,
				transactionType: 'sell'
			}),
			this.#getPropertyTransactionOffer({
				property,
				data: commonPropertyData,
				transactionType: 'rent'
			})
		].filter(offer => offer !== null);

		if (offers.length === 0) return undefined;
		if (offers.length === 1) return offers[0];

		const prices = offers
			.map(offer => Number(offer.price))
			.filter(price => !Number.isNaN(price));

		return {
			'@type': 'AggregateOffer',
			priceCurrency: 'BRL',
			lowPrice: Math.min(...prices),
			highPrice: Math.max(...prices),
			offerCount: offers.length,
			offers
		};
	}

	static #getPropertyTransactionOffer({
		property,
		data,
		transactionType
	}: {
		property: Property;
		data: Omit<Offer, 'price' | '@type'>;
		transactionType: 'sell' | 'rent';
	}): Offer | null {
		// Google's Product structured data only recognises `Offer` and
		// `AggregateOffer`; the OfferForLease/OfferForPurchase subtypes are
		// reported as "Invalid object type for field offers".
		if (transactionType === 'sell') {
			if (!property.askingPrice) return null;

			return {
				...data,
				'@type': 'Offer',
				price: property.askingPrice
			};
		}

		if (!property.rentPrice) return null;

		return {
			...data,
			'@type': 'Offer',
			price: property.rentPrice
		};
	}

	static getPropertyJsonLdImageObject(
		property: Property
	): ImageObject | undefined {
		if (!property.thumbnailUrl) return undefined;

		return {
			'@type': 'ImageObject',
			url: property.thumbnailUrl
		};
	}

	static #getPropertyCompanyJsonLdImageObject({
		company
	}: Property): ImageObject | undefined {
		const url = company.largeLogo || company.smallLogo;

		return url ? { '@type': 'ImageObject', url } : undefined;
	}

	static #getAccommodationJsonLd(
		property: Property,
		{ websiteUrl }: { websiteUrl: string }
	): AccommodationObject | ApartmentObject | HouseObject {
		const propertyFeatures = Object.values(property.featuresByCategory)
			.flat()
			.map(feature => feature.identifier);

		const launchDate = property.launchDate || undefined;

		const yearBuilt: number | undefined = launchDate?.match(/\d{4}-\d{2}-\d{2}/)
			? +(property.launchDate.split('-')[0] || 0) || undefined
			: undefined;

		const potentialAction: AccommodationObject['potentialAction'] =
			property.askingPrice || property.rentPrice
				? {
						'@type': property.askingPrice ? 'BuyAction' : 'RentAction',
						price: property.askingPrice || property.rentPrice || undefined,
						priceCurrency: 'BRL'
					}
				: undefined;

		const floorSize: QuantitativeValue | undefined = property.totalArea
			? {
					'@type': 'QuantitativeValue',
					value: property.totalArea,
					unitCode: 'NTK'
				}
			: undefined;

		const propertyPageUrl = PropertyPresenter.getPropertyPageUrl(
			property,
			websiteUrl
		);

		const commonData: Omit<AccommodationObject, '@type'> = {
			name: property.ad.title,
			description: property.ad.description,
			url: propertyPageUrl,
			address: this.#getPropertyAddressText(property),
			floorSize,
			containedInPlace: property.condoName || property.building || undefined,
			identifier: property.commercialId,
			mainEntityOfPage: propertyPageUrl,
			numberOfBathroomsTotal: property.bathrooms || undefined,
			numberOfBedrooms: property.bedrooms || undefined,
			photo: this.getPropertyJsonLdImageObject(property),
			petsAllowed: propertyFeatures.includes(FeatureEnum.PETS_ALLOWED),
			yearBuilt,
			potentialAction
		};

		return {
			...commonData,
			'@type': this.#getAccommodationType(property.propertyType.identifier)
		};
	}

	static #getPropertyAddressText(property: Property): string {
		return `${property.region}, ${property.city} - ${property.state}`;
	}

	static #getAccommodationType(
		propertyType: PropertyTypeEnum
	): AccommodationType {
		switch (propertyType) {
			case 'apartment':
				return 'Apartment';
			case 'house':
				return 'House';
			default:
				return 'Accommodation';
		}
	}
}
